import { useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

export type RequestAccessValues = {
  fullName: string;
  companyName: string;
  companyEmail: string;
  password: string;
  confirmPassword: string;
  keepLoggedIn: boolean;
};

export type RequestAccessErrors = {
  fullName?: string;
  companyName?: string;
  companyEmail?: string;
  password?: string;
  confirmPassword?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useRequestAccessForm(opts: { onSuccess: () => void }) {
  const { signUp } = useAuth();
  const [values, setValues] = useState<RequestAccessValues>({
    fullName: "",
    companyName: "",
    companyEmail: "",
    password: "",
    confirmPassword: "",
    keepLoggedIn: true,
  });

  const [errors, setErrors] = useState<RequestAccessErrors>({});
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return (
      values.fullName.trim().length > 0 &&
      values.companyName.trim().length > 0 &&
      EMAIL_REGEX.test(values.companyEmail.trim()) &&
      values.password.length >= 6 &&
      values.password === values.confirmPassword
    );
  }, [values]);

  function validate(next: RequestAccessValues): RequestAccessErrors {
    const e: RequestAccessErrors = {};

    if (!next.fullName.trim()) {
      e.fullName = "Full name is required.";
    }

    if (!next.companyName.trim()) {
      e.companyName = "Company name is required.";
    }

    const email = next.companyEmail.trim();
    if (!email) {
      e.companyEmail = "Company email is required.";
    } else if (!EMAIL_REGEX.test(email)) {
      e.companyEmail = "Enter a valid email address.";
    }

    if (!next.password) {
      e.password = "Password is required.";
    } else if (next.password.length < 6) {
      e.password = "Password must be at least 6 characters.";
    }

    if (!next.confirmPassword) {
      e.confirmPassword = "Please confirm your password.";
    } else if (next.password !== next.confirmPassword) {
      e.confirmPassword = "Passwords do not match.";
    }

    return e;
  }

  function setField<K extends keyof RequestAccessValues>(
    key: K,
    value: RequestAccessValues[K]
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));

    if (submittedOnce) {
      const next = { ...values, [key]: value };
      setErrors(validate(next));
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmittedOnce(true);
    setFormError(null);

    const nextErrors = validate(values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    try {
      setIsLoading(true);
      // Create account with user's chosen password
      await signUp(values.companyEmail.trim(), values.password);
      
      opts.onSuccess();
    } catch (err: any) {
      let message = "Account creation failed. Please try again.";
      
      if (err.code === "auth/email-already-in-use") {
        message = "An account with this email already exists.";
      } else if (err.code === "auth/invalid-email") {
        message = "Invalid email address.";
      } else if (err.code === "auth/weak-password") {
        message = "Password is too weak. Use at least 6 characters.";
      }
      
      setFormError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    values,
    errors,
    canSubmit,
    submittedOnce,
    setField,
    onSubmit,
    isLoading,
    formError,
  };
}